/* ============================================================
   EXON DROP TICKET — the drop's two cart-side surfaces.

   The perforated ticket inside the add-to-cart popup and the cart
   drawer, plus the applied badge on the cart summary. One island for
   all three nodes, because they answer the same question from the same
   fact and a second reader is a second answer.

   THIS FILE FETCHES NOTHING. Gift option controls call only the stable
   window.EXON.drops.reserveGift action; drops.js stays the sole caller of the
   drops API and exon-store.js stays the cart network owner. What arrives here
   is the item drops.js already adapted and registered — the SAME item
   the strip is drawing, run through the SAME pure stages (validate,
   compose) the strip runs. A campaign the strip has dropped can
   therefore never still be sitting in the cart.

   Wiring:
   · Data:   window.EXON.bar  (items + validate + compose + countdown)
   · Repaint on: exon:drops:state, exon:cart, either overlay shell
             opening or closing, and a 1s tick while a clock is running.
   · Output: snippets/drop-ticket.liquid zones in both shells, and the
             applied badge in snippets/exon-drawer.liquid.

   THE STATES THIS SURFACE HAS (spec §2, in precedence order), each keyed
   to the ONE fact that may select it — storefront-state-contract.ts
   APPROVED_STATE_MAP and ACCEPTED_FUTURE_CHANGES:

     redeemed   facts.applied_amount_agorot is a number — the server's
                order_application block (Shopify's reconciliation record,
                landing P2). Absent on the wire today, so unreachable
                until it lands; nothing here infers it.
     locked     facts.condition_min_agorot is a number (the server's
                condition block, also P2) AND the cart's own subtotal is
                known and below it. Presentation-only: Shopify enforces.
                An unknown subtotal never locks — a withheld benefit is
                worse than an unlocked one.
     applied    the item carries a code — the discount is in hand
     live       it does not — the drop is running
     expired    no item, but the bar island holds an ENDED one: an
                entitled item that was live on this page and closed here,
                or the bounded memory of one (client presentation memory).
     absent     nothing at all

   Not-entitled is absence. Redeemed and locked are drawn only from their
   server block, never from a label, a navigation or a guess; the arms
   that carry no block fall through to applied / live exactly as before.
   icon_stub is not a state: it is the stub's fallback when the item has
   no figure, decided after everything above.

   APPLIED MEANS EVIDENCE, NEVER INFERENCE — and this is the rule the
   first cut of this file got wrong, on the money path. The evidence is
   the item's own `code` element: the customer is holding the code, it
   came off a validated session-sourced item, and it belongs to that
   item's campaign by construction. Nothing else here is allowed to
   claim it.

   In particular the `discount_url` arm claims NOTHING. It applies the
   discount by navigating, and drops.js deliberately skips that
   navigation when no durable one-shot marker can be written — so a
   customer with storage blocked holds an open code and has had nothing
   applied. Even when the redirect does happen, whether Shopify accepted
   the code is not a fact this client is told. A held code is therefore
   not evidence of an applied discount, and telling that customer their
   drop is on the order would be a false promise about money. That arm
   renders the live treatment until there is real evidence to read.
   ============================================================ */
(function () {
  'use strict';

  if (window.__exonDropTicketInit) return;
  window.__exonDropTicketInit = true;
  window.EXON = window.EXON || {};

  /* ---------------- faults ----------------
     IF IT CATCHES, IT COUNTS AND EMITS — the same attribute and the same
     console channel drops.js and drops-bar.js use, so an operator reads one
     signal whichever part of the drops stack broke. A customer surface still
     degrades to nothing; that is the policy. What must never happen is
     "broken" and "this customer has no drop" looking identical from outside. */
  function fault(name, detail) {
    try {
      var h = document.documentElement;
      var cur = h.getAttribute('data-drops-fault') || '';
      if ((' ' + cur + ' ').indexOf(' ' + name + ' ') === -1) {
        h.setAttribute('data-drops-fault', cur ? cur + ' ' + name : name);
      }
    } catch (e) { /* no document is not a fault we can report */ }
    try { console.error('[exon:drops] ' + name, detail || ''); } catch (e) {}
  }

  /* ---------------- tiny DOM helpers ---------------- */
  function all(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function zone(root, name) { return root.querySelector('[data-dtk-' + name + ']'); }
  function setText(node, value) {
    if (!node) return;
    if (node.textContent !== value) node.textContent = value;
  }
  function show(node, on) { if (node) node.hidden = !on; }
  function flag(node, name, on) {
    if (!node) return;
    if (on) { if (!node.hasAttribute(name)) node.setAttribute(name, ''); }
    else node.removeAttribute(name);
  }

  /* ============================================================
     derive — PURE. No DOM, no globals, and no clock of its own: it takes the
     item the registry already composed, the moment to measure against, and
     the countdown to format with.

     Returns what the three nodes should show, or the REASON there is nothing
     to show. The reason is not decoration: "nothing" is the majority answer
     on this surface, and an operator has to be able to tell a misconfigured
     campaign apart from a customer who simply has no drop.

     Split out of the island for the same reason the bar splits validate and
     compose out of its renderer — the decisions worth testing are these, and
     they must be reachable without a DOM.
     ============================================================ */
  function derive(item, nowMs, countdown, ctx) {
    ctx = ctx || {};
    if (!item || typeof item !== 'object' || !Array.isArray(item.elements)) {
      /* the expired treatment: no live item, but this page saw one close */
      var en = ctx.ended;
      if (en && typeof en === 'object' && Array.isArray(en.elements)) {
        var epct = '';
        for (var k = 0; k < en.elements.length; k++) {
          if (en.elements[k] && en.elements[k].type === 'highlight') epct = en.elements[k].pct;
        }
        return {
          reason: '',
          view: {
            id: en.id, state: 'expired', headline: '', pct: epct, code: '',
            applied: false, left: '', urgent: false, amount: ''
          }
        };
      }
      return { view: null, reason: 'no_item' };
    }

    var e = {};
    for (var i = 0; i < item.elements.length; i++) {
      if (item.elements[i]) e[item.elements[i].type] = item.elements[i];
    }

    /* A ticket is a headline on a stub. Without one there is a coloured
       rectangle and a clock, which is worse than nothing. */
    if (!e.headline || !e.headline.text) return { view: null, reason: 'headline_missing' };

    /* THE CHOSEN MECHANIC WINS, AND ONLY WHEN IT HAS A REPLACEMENT TO SHOW.
       An Anchor or Mystery campaign carries a headline for the strip, so
       without this the ticket would draw itself for a campaign whose offer is
       a case or a deck — and the popup would show BOTH, each describing the
       same drop differently. The approved decision is explicit: a choice drop
       REPLACES the ticket, because a gift drop has no percentage to put on the
       stub and "the drop is active" plus "pick a gift" is one campaign
       announced twice for 130px.

       IT IS CONDITIONAL ON A VALID REPLACEMENT, and that is the whole reason
       this is a ctx flag rather than a mechanic string. If the choice island
       is not installed, its writer is missing, or its state is not renderable,
       standing down here would leave the customer with NOTHING for a campaign
       they are entitled to. The islands answer whether they are actually
       drawing; this surface stands down only for a surface that exists. */
    if (ctx.choiceReplacement === true) return { view: null, reason: 'choice_campaign' };

    /* THE CLOCK. Formatted by the bar island's countdown, from the ITEM's
       window.end_at — the same field compose() uses to decide whether the item
       exists at all. One source of time, so the ticket cannot show a campaign
       the gate has already closed, and empty at zero, so it renders as gone
       rather than as a negative number. */
    var left = countdown(item.window ? item.window.end_at : null, nowMs) || '';

    /* THE ONLY EVIDENCE OF AN APPLIED DISCOUNT THIS CLIENT HAS. The code
       element rides a validated session-sourced item and belongs to that
       item's campaign by construction, so there is no global to match against
       and no adjacent fact to infer from. See the header for why the
       discount_url arm deliberately claims nothing. */
    var code = e.code ? e.code.value : '';

    /* the lifecycle, in the contract's precedence: order_application >
       condition_unmet > applied / live */
    var f = (item.facts && typeof item.facts === 'object') ? item.facts : {};
    var shared = sharedCoupon(f);
    var weekend = weekendOffer(f);
    if (f.weekend_offer && !weekend) return { view: null, reason: 'weekend_usage_invalid' };
    if (weekend && weekend.pending) code = '';
    if (f.shared_coupon && !shared) return { view: null, reason: 'shared_usage_invalid' };
    var sharedReady = shared && shared.member_state === 'ready' &&
      /^(ready|closing)$/.test(shared.node_state) && Date.parse(shared.expires_at) > nowMs;
    if (shared && !sharedReady) code = '';
    var state = code ? 'applied' : 'live';
    var amount = '';
    if (isAgorot(f.applied_amount_agorot)) {
      state = 'redeemed';
      amount = money(f.applied_amount_agorot);
    } else if (shared) {
      if (/^(expired|refused)$/.test(shared.member_state) || Date.parse(shared.expires_at) <= nowMs) {
        return { view: null, reason: 'shared_usage_unavailable' };
      }
      state = shared.node_state === 'stopped' ? 'shared-stopped' :
        (sharedReady ? 'shared-ready' : 'shared-pending');
    } else if (isAgorot(f.condition_min_agorot) && isAgorot(ctx.cartTotalAgorot) &&
               ctx.cartTotalAgorot < f.condition_min_agorot) {
      state = 'locked';
      amount = money(f.condition_min_agorot);
    }

    return {
      reason: '',
      view: {
        id: item.id,
        state: state,
        headline: e.headline.text,
        /* already formatted by drops.js ("-30%"), never a raw number */
        pct: e.highlight ? e.highlight.pct : '',
        code: code,
        applied: !!code,
        /* the figure the sub-line carries: the threshold when locked, the
           saving when redeemed, nothing otherwise */
        amount: amount,
        weekend: weekend,
        shared: shared ? {
          state: state === 'shared-stopped' ? 'stopped' : (sharedReady ? 'ready' : 'intended'),
          checkout_cap: shared.checkout_cap
        } : null,
        left: left,
        /* URGENT is read off the RENDERED clock rather than re-derived from
           end_at, because a second parse of that timestamp is a second answer
           waiting to happen. A leading `00:` is exactly "under an hour": a
           longer window has a non-zero hour field, and a multi-day one is
           prefixed with its day count. */
        urgent: /^00:/.test(left)
      }
    };
  }

  function isAgorot(n) {
    return typeof n === 'number' && isFinite(n) && n >= 0 && Math.floor(n) === n;
  }
  function weekendOffer(facts) {
    var u = facts && facts.weekend_offer;
    var o = u && u.observed;
    if (!u || !/^weekend_(single_use|reusable)_v1$/.test(u.policy || '') ||
        u.reusable !== (u.policy === 'weekend_reusable_v1') ||
        !/^(pending|ready|expired|refused)$/.test(u.member_state || '') ||
        !o || !Number.isSafeInteger(o.applications) || o.applications < 0 ||
        !Number.isSafeInteger(o.total_amount_agorot) || o.total_amount_agorot < 0) return null;
    return { reusable: u.reusable, pending: u.member_state === 'pending',
      applications: o.applications, total_amount_agorot: o.total_amount_agorot };
  }
  function sharedCoupon(facts) {
    var u = facts && facts.shared_coupon;
    if (!u || typeof u !== 'object' || u.policy !== 'shared_checkout_cap_v1' ||
        !Number.isSafeInteger(u.checkout_cap) || u.checkout_cap < 1 ||
        u.once_per_customer !== true || u.reserves_a_place !== false ||
        !/^(intended|ready|closing|stopped)$/.test(u.node_state || '') ||
        !/^(pending|ready|expired|refused)$/.test(u.member_state || '') ||
        typeof u.expires_at !== 'string' || isNaN(Date.parse(u.expires_at))) return null;
    return u;
  }
  /* agorot to shekels, the way the spec writes money: "150 ₪", "21.60 ₪".
     A plain ASCII hyphen never appears here and the run is bidi-isolated by
     the .ltr node it is written into. */
  function money(agorot) {
    var whole = Math.floor(agorot / 100);
    var frac = agorot % 100;
    return (frac ? whole + '.' + (frac < 10 ? '0' : '') + frac : String(whole)) + ' \u20AA';
  }

  /* ------------------------------------------------------------
     THE ONE READ.

     window.EXON.bar is the registry the strip installs. Requiring all four
     keeps the dependency to ONE object: if the bar island never installed
     there are no items to read anyway, so no bar means no ticket, which is
     the correct degradation and not a special case.
     ------------------------------------------------------------ */
  function bar() {
    var b = window.EXON.bar;
    if (!b) return null;
    if (typeof b.items !== 'function' || typeof b.validate !== 'function') return null;
    if (typeof b.compose !== 'function' || typeof b.countdown !== 'function') return null;
    return b;
  }

  /* Same contract, same validation, same expiry gate as the strip. Picking the
     item is the only thing this does that the strip does not: the ticket is a
     DROP surface, so an ambient or engine-sourced item is somebody else's
     business. `source === 'session'` restates, at the point of selection, the
     same lock the bar puts on the code element — a code may only ever ride a
     session item, and this surface must not be the place that weakens it. */
  function currentItem(b, nowMs) {
    var raw = b.items();
    var ok = [];
    for (var i = 0; i < raw.length; i++) {
      var v = b.validate(raw[i]);
      if (v) ok.push(v);
    }
    var set = b.compose(ok, nowMs);
    for (var j = 0; j < set.length; j++) {
      if (set[j].band === 'entitled' && set[j].source === 'session') return set[j];
    }
    return null;
  }

  /* the cart's own subtotal, in agorot, when the store island has one.
     Advisory: it decides only whether the locked treatment is DRAWN. */
  function cartTotal() {
    var cart = window.EXON.cart && window.EXON.cart.state;
    var t = cart ? cart.total_price : null;
    return (typeof t === 'number' && isFinite(t)) ? t : null;
  }

  /* the impure wrapper: reads the globals, hands the pure stage its inputs */
  /* IS A CHOICE SURFACE ACTUALLY DRAWING RIGHT NOW?

     Asked of the islands themselves rather than inferred from the mechanic
     string, because "this is an Anchor campaign" and "the customer can see an
     Anchor case" are different facts and only the second one earns this
     surface's silence. Each island publishes `drawing()`; an island that is
     not loaded answers nothing and the ticket keeps its place. */
  function choiceReplacement() {
    var a = window.ExonDropAnchor;
    var m = window.ExonDropMystery;
    if (a && typeof a.drawing === 'function' && a.drawing()) return true;
    if (m && typeof m.drawing === 'function' && m.drawing()) return true;
    return false;
  }

  function current(nowMs) {
    var b = bar();
    if (!b) return { view: null, reason: 'no_registry' };
    try {
      var ctx = {
        ended: (typeof b.ended === 'function') ? b.ended() : null,
        cartTotalAgorot: cartTotal(),
        choiceReplacement: choiceReplacement()
      };
      return derive(currentItem(b, nowMs), nowMs, b.countdown, ctx);
    } catch (e) {
      /* the registry answering with a throw is invisible to the customer and
         must not be invisible to an operator: it means an entitled member is
         looking at a cart with no drop in it. */
      fault('ticket_read_failed', e);
      return { view: null, reason: 'read_failed' };
    }
  }

  /* ------------------------------------------------------------
     Reporting an absence.

     An entitled member looking at a cart with no ticket in it is a different
     incident from a visitor who has no drop, and from outside they are
     identical. So every reason a ticket is missing lands here, and exactly
     ONE of them is legitimate: the campaign's window has closed, which is
     what "redeemed and expired render as absence" means.
     ------------------------------------------------------------ */
  function state() {
    var d = window.EXON.drops;
    return (d && d.state) || null;
  }
  function entitled() {
    var s = state();
    return !!(s && s.entitled);
  }

  /* Read through the SAME countdown the ticket renders with, so "the window
     has closed" cannot mean one thing to the clock and another to this check.
     A campaign carrying no end_at has no window to close, so an absence there
     is never the legitimate kind. */
  function windowClosed(nowMs) {
    var b = bar();
    var s = state();
    if (!b || !s || !s.window_end_at) return false;
    return b.countdown(s.window_end_at, nowMs) === null;
  }

  function report(reason, nowMs) {
    if (!reason) return;
    /* no drop is not an incident */
    if (!entitled()) return;
    if (reason === 'no_registry') { fault('ticket_bar_unavailable', ''); return; }
    if (reason === 'read_failed') return;      /* already emitted, with the throw */
    if (windowClosed(nowMs)) return;           /* absence IS the state */
    var s = state();
    var id = (s && s.campaign_id) || '';
    /* the specific cause where there is one, the general "an entitled session
       renders nothing" where there is not — both are a campaign that reached a
       customer and could not be drawn */
    fault(reason === 'headline_missing' ? 'ticket_headline_missing' : 'ticket_not_renderable', id);
  }

  /* ------------------------------------------------------------
     painting. Every node is driven from the same view object, so the popup,
     the drawer and the badge cannot disagree about what the drop is.
     ------------------------------------------------------------ */
  function paintTicket(mount, v) {
    if (!v) { mount.hidden = true; return; }

    mount.setAttribute('data-dtk-state', v.state || (v.applied ? 'applied' : 'live'));
    flag(mount, 'data-dtk-urgent', v.urgent);

    /* the stub carries the percentage, or the campaign's icon when there is no
       percentage to show */
    var pct = zone(mount, 'pct');
    setText(pct, v.pct);
    show(pct, !!v.pct);
    show(zone(mount, 'ic'), !v.pct);
    /* the stub is the icon stub: CSS keys the stub's own sub-line on it */
    flag(mount, 'data-dtk-stub', !v.pct);

    /* the headline: the campaign's own line while it runs; the theme's own
       closing lines for the lifecycle states the server never words
       (locked, redeemed, expired), carried on the mount as
       data-dtk-copy-<state> so the copy stays in Liquid */
    var own = mount.getAttribute('data-dtk-copy-' + v.state) || '';
    setText(zone(mount, 'headline'), own || v.headline);

    /* the sub-line and its figure — the state's spans are CSS-switched */
    var sub = zone(mount, 'sub');
    show(sub, v.state === 'locked' || v.state === 'redeemed' || v.state === 'expired' ||
              (v.state === 'live' && !v.pct));
    var amt = zone(mount, 'amount');
    setText(amt, v.amount);
    show(amt, !!v.amount);

    var codeBtn = zone(mount, 'code');
    /* a redeemed discount is on the order: the code has done its job and a
       chip inviting a second paste would be noise */
    var showCode = !!v.code && v.state !== 'redeemed';
    if (codeBtn) {
      setText(codeBtn, v.code);
      show(codeBtn, showCode);
      if (showCode) bindCopy(codeBtn);
    }

    var sharedUsage = zone(mount, 'shared');
    if (sharedUsage) {
      var usageCopy = '';
      if (v.shared) {
        usageCopy = sharedUsage.getAttribute('data-dtk-copy-' + v.shared.state) || '';
        usageCopy = usageCopy.replace('{cap}', String(v.shared.checkout_cap));
      }
      setText(sharedUsage, usageCopy);
      show(sharedUsage, !!usageCopy);
    }

    var weekendUsage = zone(mount, 'weekend');
    if (weekendUsage) {
      var weekendText = '';
      if (v.weekend) {
        var weekendKey = v.weekend.pending ? 'pending' : v.weekend.reusable ? 'reusable' : 'single';
        weekendText = weekendUsage.getAttribute('data-dtk-copy-' + weekendKey) || '';
        if (v.weekend.applications > 0) weekendText += ' ' +
          (weekendUsage.getAttribute('data-dtk-copy-observed') || '')
            .replace('{count}', String(v.weekend.applications))
            .replace('{amount}', money(v.weekend.total_amount_agorot));
      }
      setText(weekendUsage, weekendText);
      show(weekendUsage, !!weekendText);
    }

    /* the close line: the running clock, or "saved until payment" once the
       discount is on the order; nothing at all once it has closed */
    setText(zone(mount, 'countdown'), v.left);
    show(zone(mount, 'when'), v.state !== 'shared-stopped' && (!!v.left || v.state === 'redeemed'));

    var weekendMode = paintWeekend(mount);
    var giftMode = paintLaunch(mount) || paintGift(mount);
    if (giftMode) {
      show(codeBtn, false);
      show(sub, false);
    }
    flag(mount, 'data-dtk-weekend-mode', weekendMode);

    mount.hidden = false;
  }

  function paintBadge(node, v) {
    var cart = window.EXON.cart && window.EXON.cart.state;
    var count = cart ? Number(cart.item_count) : 0;
    /* The badge carries the customer's code to the checkout step, so it shows
       only where there IS one — the same evidence rule the ticket runs on.
       And it is a statement about THIS ORDER, so an empty cart clears it
       synchronously, in the same pass the cart event arrives in, rather than a
       frame later with a discount still claimed against nothing. Expiry and a
       lost entitlement clear it the same way: v goes null and so does this. */
    if (!v || !v.code || v.state === 'redeemed' || !(count > 0)) { node.hidden = true; return; }

    var btn = node.querySelector('[data-dtk-badge-code]');
    if (btn) {
      setText(btn, v.code);
      show(btn, true);
      bindCopy(btn);
    }
    node.hidden = false;
  }

  /* the strip's copy handler, not a second one: same clipboard path, same
     select-on-refusal fallback, same [data-bar-copied] acknowledgement, same
     announcement, same faults. Idempotent, so calling it on every repaint
     never stacks listeners. */
  function bindCopy(node) {
    var b = bar();
    if (b && typeof b.bindCopy === 'function') b.bindCopy(node);
  }

  function giftState() {
    var d = window.EXON.drops;
    if (!d) return null;
    var s = (typeof d.get === 'function') ? d.get() : d.state;
    return (s && s.entitled && s.gift && Array.isArray(s.gift.options)) ? s.gift : null;
  }

  function giftAction() {
    var d = window.EXON.drops;
    return (d && typeof d.getGiftAction === 'function') ? d.getGiftAction() : { state: 'idle', pending: false };
  }

  function giftClosed(g) {
    if (!g || g.closed) return true;
    var t = g.cutoff_at ? Date.parse(g.cutoff_at) : NaN;
    return !isNaN(t) && t <= Date.now();
  }

  function copyOn(root, name) { return root ? (root.getAttribute('data-dtk-copy-' + name) || '') : ''; }

  function weekendState() {
    var d = window.EXON.drops;
    if (!d) return null;
    var s = typeof d.get === 'function' ? d.get() : d.state;
    return s && s.entitled && s.mechanic === 'weekend' && s.weekend_event &&
      Array.isArray(s.weekend_event.offers) ? s.weekend_event : null;
  }

  function weekendAction() {
    var d = window.EXON.drops;
    return d && typeof d.getWeekendAction === 'function'
      ? d.getWeekendAction() : { state: 'idle', pending: false };
  }

  function weekendTerms(root, offer) {
    var usage = offer.native_usage;
    var conditions = offer.conditions;
    var scope = '';
    if (offer.kind === 'percentage') {
      if (conditions.scope_kind === 'all') scope = copyOn(root, 'scope-all');
      else if (conditions.scope_kind === 'collections') {
        scope = copyOn(root, 'scope-collections').replace('{count}', String(conditions.scope_collection_gids.length));
      } else {
        scope = copyOn(root, 'scope-variants').replace('{count}', String(conditions.scope_variant_gids.length));
      }
    } else {
      var minimum = null;
      for (var i = 0; i < conditions.options.length; i++) {
        var value = conditions.options[i].minimum_subtotal_agorot;
        if (Number.isSafeInteger(value) && (minimum === null || value < minimum)) minimum = value;
      }
      scope = copyOn(root, 'scope-collection').replace('{collection}', conditions.handle);
      if (minimum !== null && minimum > 0) scope += ' ' + copyOn(root, 'minimum').replace('{amount}', money(minimum));
    }
    var use = offer.kind === 'gift' ? copyOn(root, 'gift-use') :
      copyOn(root, usage.reusable ? 'reusable' : 'single-use');
    return scope + ' ' + use + ' ' + copyOn(root, 'expires').replace('{date}', weekendCutoff(offer.ends_at));
  }

  function weekendCutoff(value) {
    var timestamp = Date.parse(value);
    if (isNaN(timestamp)) return value;
    try {
      var lang = document.documentElement && document.documentElement.getAttribute('lang');
      return new Intl.DateTimeFormat(lang || undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        timeZoneName: 'short'
      }).format(timestamp);
    } catch (e) {
      // The normalized ISO timestamp remains exact, including its intraday time.
      return String(value).replace('T', ' ').replace(/\.000(?=Z|[+-]\d\d:\d\d$)/, '');
    }
  }

  function paintWeekend(mount) {
    var root = zone(mount, 'weekend-menu');
    var menu = weekendState();
    if (!root || !menu) { show(root, false); return false; }
    var options = zone(mount, 'weekend-options');
    var action = weekendAction();
    setText(zone(mount, 'weekend-title'), copyOn(root, menu.acquisition_open ? 'title-open' : 'title-held'));
    var signature = menu.campaign_version_id + ':' + menu.selected_offer_key + ':' + menu.acquisition_open;
    for (var i = 0; i < menu.offers.length; i++) {
      var part = menu.offers[i];
      signature += '|' + part.offer_key + ':' + part.kind + ':' + part.title + ':' + part.ends_at + ':' +
        part.availability.claimable + ':' + part.availability.remaining + ':' +
        (part.held ? part.held.state : '-') + ':' + part.selected;
    }
    if (options && options.getAttribute('data-dtk-weekend-signature') !== signature) {
      while (options.firstChild) options.removeChild(options.firstChild);
      for (var n = 0; n < menu.offers.length; n++) {
        var offer = menu.offers[n];
        var btn = document.createElement('button');
        btn.type = 'button'; btn.className = 'dtk-weekend-option';
        btn.setAttribute('data-dtk-weekend-offer', offer.offer_key);
        var heading = document.createElement('b');
        heading.textContent = offer.title || (offer.kind === 'percentage'
          ? String(offer.native_usage.pct) + '%' : copyOn(root, 'gift'));
        var terms = document.createElement('small');
        terms.textContent = weekendTerms(root, offer);
        var availability = document.createElement('span');
        var stateKey = offer.selected ? 'selected' : offer.held ? offer.held.state :
          (menu.acquisition_open && offer.availability.claimable ? 'available' : 'unavailable');
        availability.textContent = copyOn(root, stateKey);
        if (!offer.held && menu.acquisition_open && offer.availability.claimable) {
          availability.textContent += ' ' + copyOn(root, 'remaining').replace('{count}', String(offer.availability.remaining));
        }
        btn.appendChild(heading); btn.appendChild(terms); btn.appendChild(availability);
        (function (key, actionButton) {
          actionButton.addEventListener('click', function () {
            var api = window.EXON.drops;
            if (actionButton.disabled || !api) return;
            var action = actionButton.getAttribute('data-held') !== null
              ? api.selectWeekendOffer : api.claimWeekendOffer;
            if (typeof action !== 'function') return;
            actionButton.disabled = true;
            Promise.resolve(action.call(api, key)).catch(function (e) { fault('weekend_action_failed', e); });
          });
        })(offer.offer_key, btn);
        options.appendChild(btn);
      }
      options.setAttribute('data-dtk-weekend-signature', signature);
    }
    if (options) for (var j = 0; j < menu.offers.length; j++) {
      var row = menu.offers[j], button = options.children[j];
      if (!button) continue;
      var active = Date.parse(row.ends_at) > Date.now() && !row.selected;
      var selectable = active && ((!!row.held && /^(pending|ready)$/.test(row.held.state)) ||
        (!row.held && menu.acquisition_open && row.availability.claimable && row.availability.remaining > 0));
      button.disabled = !selectable || !!action.pending;
      flag(button, 'data-selected', row.selected);
      flag(button, 'data-held', !!row.held);
    }
    var statusKey = action.state && action.state !== 'idle' ? action.state : '';
    setText(zone(mount, 'weekend-status'), statusKey ? copyOn(root, statusKey) : '');
    show(zone(mount, 'weekend-status'), !!statusKey);
    show(root, true);
    return true;
  }

  function paintLaunch(mount) {
    var s = state(), d = window.EXON.drops;
    var u = s && s.entitled && s.mechanic === 'launch_override' && s.usage;
    var root = zone(mount, 'gift');
    if (!root || !u || u.policy !== 'launch_bxgy_v1') return false;
    var a = d && typeof d.getLaunchAction === 'function' ? d.getLaunchAction() : { state: 'idle', pending: false };
    var closed = Date.parse(u.expires_at) <= Date.now() || u.node_state === 'stopped' ||
      /^(expired|refused)$/.test(u.member_state) || /^(redeemed|expired|revoked|consumed|invalid|void)$/.test(s.code_state || '');
    var ready = !closed && u.member_state === 'ready' && /^(ready|closing)$/.test(u.node_state) && !!s.code;
    var options = zone(mount, 'options');
    var signature = 'launch:' + s.campaign_version_id + ':' + u.reward_issue_id + ':' + (u.gift_title || '');
    if (options && options.getAttribute('data-dtk-options-signature') !== signature) {
      while (options.firstChild) options.removeChild(options.firstChild);
      var btn = document.createElement('button');
      btn.type = 'button'; btn.className = 'dtk-gift-option';
      btn.setAttribute('data-dtk-launch-gift', u.reward_issue_id);
      btn.textContent = copyOn(root, 'launch-add') + (u.gift_title ? ': ' + u.gift_title : '');
      btn.addEventListener('click', function () {
        var api = window.EXON.drops;
        if (btn.disabled || !api || typeof api.attachLaunchGift !== 'function') return;
        btn.disabled = true;
        Promise.resolve(api.attachLaunchGift()).catch(function (e) { fault('launch_action_failed', e); });
      });
      options.appendChild(btn);
      options.setAttribute('data-dtk-options-signature', signature);
    }
    if (options && options.children[0]) options.children[0].disabled = !ready || !!a.pending || a.state === 'done';
    setText(zone(mount, 'gift-title'), copyOn(root, 'launch-terms'));
    var key = closed ? 'closed' : !ready ? 'launch-pending' :
      a.state === 'refused' && a.code === 'existing_discount' ? 'launch-discount' :
      a.state === 'refused' && a.code === 'cart_restore_unknown' ? 'launch-review' :
      a.state !== 'idle' ? a.state : '';
    var terms = copyOn(root, u.use_cap === null ? 'launch-uncapped' : 'launch-cap').replace('{cap}', String(u.use_cap));
    setText(zone(mount, 'gift-status'), (key ? copyOn(root, key) + ' ' : '') + terms);
    show(zone(mount, 'gift-status'), true);
    flag(mount, 'data-dtk-gift-mode', true);
    show(root, true);
    return true;
  }

  /* THE GIFT TERMS RULE. Valid terms are two safe integers with a positive
     advertised threshold and an enforced minimum at or above it (the legacy
     `enforced >= advertised` rule normalizeGiftGoal also holds). Inverted,
     zero, missing or fractional terms are invalid: no amount, disabled.
     Returns the ONE amount line to show, or '' when the terms are not valid
     or the copy that would describe them is absent. */
  function giftAmountLine(root, o) {
    var minimum = o && o.minimum_subtotal_agorot;
    var threshold = o && o.threshold_agorot;
    if (!Number.isSafeInteger(minimum) || !Number.isSafeInteger(threshold) ||
        threshold <= 0 || minimum < threshold) return '';
    if (minimum === threshold) {
      var single = copyOn(root, 'minimum');
      return single ? single.replace('{amount}', money(minimum)) : '';
    }
    var legacy = copyOn(root, 'minimum-legacy');
    return legacy ? legacy.replace('{advertised}', money(threshold)).replace('{enforced}', money(minimum)) : '';
  }

  function paintGift(mount) {
    var root = zone(mount, 'gift');
    var g = giftState();
    if (!root || !g) {
      show(root, false);
      flag(mount, 'data-dtk-gift-mode', false);
      return false;
    }
    flag(mount, 'data-dtk-gift-mode', true);
    var a = giftAction();
    var held = g.held;
    var closed = giftClosed(g);
    var title = zone(mount, 'gift-title');
    var options = zone(mount, 'options');
    var status = zone(mount, 'gift-status');
    setText(title, held ? copyOn(root, held.state === 'consumed' ? 'consumed' : 'held') : copyOn(root, closed ? 'closed' : 'choose'));
    if (options) {
      var signature = '';
      for (var i = 0; i < g.options.length; i++) {
        signature += (signature ? '|' : '') + g.options[i].option_ordinal + ':' + g.options[i].variant_gid + ':' + g.options[i].title + ':' + g.options[i].remaining + ':' + g.options[i].threshold_agorot + ':' + g.options[i].minimum_subtotal_agorot;
      }
      if (options.getAttribute('data-dtk-options-signature') !== signature) {
        while (options.firstChild) options.removeChild(options.firstChild);
        for (var n = 0; n < g.options.length; n++) {
          var built = g.options[n];
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'dtk-gift-option';
          btn.setAttribute('data-dtk-gift-option', built.variant_gid);
          btn.setAttribute('title', built.title);
          var remaining = document.createElement('small');
          remaining.textContent = copyOn(root, 'remaining') + ' ' + built.remaining;
          btn.textContent = built.title;
          btn.appendChild(remaining);
          /* ONE amount line. Equal terms keep today's single minimum; a valid
             legacy pair (enforced above advertised) names BOTH numbers in one
             sentence, advertised first and enforced as the checkout
             condition — never a lone amount that implies qualification below
             the enforced one. Without the legacy copy key (an older snippet)
             an unequal pair shows no amount, exactly as before. */
          var amountLine = giftAmountLine(root, built);
          if (amountLine) {
            var minimum = document.createElement('span');
            minimum.className = 'dtk-gift-minimum';
            minimum.textContent = amountLine;
            btn.appendChild(minimum);
          }
          (function (gid, actionButton) {
            actionButton.addEventListener('click', function () {
              var d = window.EXON.drops;
              if (actionButton.disabled || !d || typeof d.reserveGift !== 'function') return;
              actionButton.disabled = true;
              Promise.resolve(d.reserveGift(gid)).catch(function (e) { fault('gift_action_failed', e); });
            });
          })(built.variant_gid, btn);
          options.appendChild(btn);
        }
        options.setAttribute('data-dtk-options-signature', signature);
      }
      for (var j = 0; j < g.options.length; j++) {
        var o = g.options[j];
        var optionButton = options.children && options.children[j];
        if (!optionButton) continue;
        var chosen = !!held && held.option_ordinal === o.option_ordinal && held.variant_gid === o.variant_gid;
        flag(optionButton, 'data-selected', chosen);
        var termsValid = !!giftAmountLine(root, o);
        var canReserve = termsValid && !held && g.actions && g.actions.reserve_allowed && o.available;
        var canAttach = termsValid && chosen && held.state === 'reserved' && g.actions && g.actions.cart_attach_allowed;
        optionButton.disabled = !(!closed && !a.pending && (canReserve || canAttach));
      }
    }
    var statusKey = '';
    if (held && held.state === 'consumed') statusKey = 'consumed';
    else if (closed) statusKey = 'closed';
    else if (a.state === 'refused' && a.code === 'cart_restore_unknown') statusKey = 'cart-review';
    else if (a.state === 'refused' && a.code === 'existing_discount') statusKey = 'existing-discount';
    else if (a.state && a.state !== 'idle') statusKey = a.state;
    else if (held) statusKey = 'held';
    var chosenTitle = '';
    if (held) {
      for (var k = 0; k < g.options.length; k++) if (g.options[k].option_ordinal === held.option_ordinal) chosenTitle = g.options[k].title;
    }
    setText(status, statusKey ? copyOn(root, statusKey) + (statusKey === 'held' && chosenTitle ? ': ' + chosenTitle : '') : '');
    show(status, !!statusKey);
    show(root, true);
    return true;
  }

  /* ------------------------------------------------------------
     the loop
     ------------------------------------------------------------ */
  var mounts = [];
  var badges = [];
  var shells = [];
  var ticker = null;

  function anyOpen() {
    for (var i = 0; i < shells.length; i++) if (!shells[i].hidden) return true;
    return false;
  }

  /* One second-resolution repaint, alive only while a clock is actually
     counting AND a surface is actually on screen. The strip already ticks for
     the page as a whole; these nodes are hidden most of a page view, and
     ticking a hidden node is work nobody can see. */
  function retime(v) {
    var need = !!(v && v.left) && anyOpen();
    if (need && !ticker) ticker = setInterval(repaint, 1000);
    else if (!need && ticker) { clearInterval(ticker); ticker = null; }
  }

  function repaint() {
    var now = Date.now();
    var r = current(now);
    if (!r.view) report(r.reason, now);

    var i;
    for (i = 0; i < mounts.length; i++) paintTicket(mounts[i], r.view);
    for (i = 0; i < badges.length; i++) paintBadge(badges[i], r.view);
    retime(r.view);
  }

  function boot() {
    /* no document is not a failure — it is a load with nothing to drive, which
       is also how this file is reachable for testing its pure stage */
    if (typeof document === 'undefined' || !document.querySelectorAll) return;

    mounts = all('[data-drop-ticket]');
    badges = all('[data-drop-badge]');
    /* neither surface is on this page */
    if (!mounts.length && !badges.length) return;

    /* The overlay shells the mounts live inside. Their [hidden] flag is the
       only "am I on screen" signal upsell.js publishes, and watching the
       attribute keeps this island uncoupled from that one: no import, no
       handshake, nothing to keep in step if the popup's open path changes.
       No subtree, so the mounts this island hides and shows cannot re-trigger
       their own observer. */
    shells = all('[data-exon-popup], [data-exon-drawer]');
    if (window.MutationObserver && shells.length) {
      var mo = new MutationObserver(function () { repaint(); });
      for (var i = 0; i < shells.length; i++) {
        mo.observe(shells[i], { attributes: true, attributeFilter: ['hidden'] });
      }
    }

    /* the bus is document events (exon-store.js), so listening directly costs
       no wait for that island to exist — same as drops-bar.js does for the
       settle signal */
    document.addEventListener('exon:drops:state', repaint);
    document.addEventListener('exon:drops:gift-action', repaint);
    document.addEventListener('exon:drops:weekend-action', repaint);
    document.addEventListener('exon:drops:launch-action', repaint);
    document.addEventListener('exon:drops:ended', repaint);
    document.addEventListener('exon:cart', repaint);

    repaint();
  }

  try {
    boot();
  } catch (e) {
    /* a synchronous throw here means the ticket never ran at all, which the
       customer cannot see and an operator must */
    fault('ticket_boot_threw', e);
  }

  /* exported for direct testing, the same way the bar exports its stages.
     `derive` is the pure one: (item, nowMs, countdown) in, {view, reason} out,
     no DOM and no globals touched. */
  window.ExonDropTicket = { derive: derive, repaint: repaint };
})();
